// Copyright 2021 The Casdoor Authors. All Rights Reserved.
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
//      http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import * as Setting from "../conf/Setting";
import i18next from "i18next";
import FileSaver from "file-saver";

export function getGlobalUsers(page, pageSize, field = "", value = "", sortField = "", sortOrder = "") {
  return fetch(`${Setting.ServerUrl}/api/get-global-users?p=${page}&pageSize=${pageSize}&field=${field}&value=${value}&sortField=${sortField}&sortOrder=${sortOrder}`, {
    method: "GET",
    credentials: "include",
  }).then(res => res.json());
}

export function getUsers(owner, page = "", pageSize = "", field = "", value = "", sortField = "", sortOrder = "") {
  return fetch(`${Setting.ServerUrl}/api/get-users?owner=${owner}&p=${page}&pageSize=${pageSize}&field=${field}&value=${value}&sortField=${sortField}&sortOrder=${sortOrder}`, {
    method: "GET",
    credentials: "include",
  }).then(res => res.json());
}

export function getOrganizationUsers(orgOwner, organId, isDirectly, page = "", pageSize = "", field = "", value = "", sortField = "", sortOrder = "") {
  return fetch(`${Setting.ServerUrl}/api/get-users-with-organ?orgOwner=${orgOwner}&organUid=${organId}&isDirectly=${isDirectly}&p=${page}&pageSize=${pageSize}&field=${field}&value=${value}&sortField=${sortField}&sortOrder=${sortOrder}`, {
    method: "GET",
    credentials: "include",
  }).then(res => res.json());
}

export function getDepartmentUsers(depId, isDirectly, page = "", pageSize = "", field = "", value = "", sortField = "", sortOrder = "") {
  return fetch(`${Setting.ServerUrl}/api/get-users-with-dept?depId=${depId}&isDirectly=${isDirectly}&p=${page}&pageSize=${pageSize}&field=${field}&value=${value}&sortField=${sortField}&sortOrder=${sortOrder}`, {
    method: "GET",
    credentials: "include",
  }).then(res => res.json());
}

export function getOrganizationUserCount(uid) {
  return fetch(`${Setting.ServerUrl}/api/get-organization-number?organUid=${uid}`, {
    method: "GET",
    credentials: "include",
  }).then(res => res.json());
}

export function getDepartmentUserCount(uid) {
  return fetch(`${Setting.ServerUrl}/api/get-department-number?uid=${uid}`, {
    method: "GET",
    credentials: "include",
  }).then(res => res.json());
}

export function getUser(owner, name) {
  return fetch(`${Setting.ServerUrl}/api/get-user?id=${owner}/${encodeURIComponent(name)}`, {
    method: "GET",
    credentials: "include",
  }).then(res => res.json());
}

export function updateUser(owner, name, user) {
  const newUser = Setting.deepCopy(user);
  return fetch(`${Setting.ServerUrl}/api/update-user?id=${owner}/${encodeURIComponent(name)}`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(newUser),
  }).then(res => res.json());
}

export function addUser(user) {
  const newUser = Setting.deepCopy(user);
  return fetch(`${Setting.ServerUrl}/api/add-user`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(newUser),
  }).then(res => res.json());
}

export function addDepartmentUser(user, depId) {
  const newUser = Setting.deepCopy(user);
  return fetch(`${Setting.ServerUrl}/api/add-user-with-dept?depId=${depId}`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(newUser),
  }).then(res => res.json());
}

export function addOrganizationUser(user, organId) {
  const newUser = Setting.deepCopy(user);
  return fetch(`${Setting.ServerUrl}/api/add-user-with-dept?organId=${organId}`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(newUser),
  }).then(res => res.json());
}

export function deleteUser(user) {
  const newUser = Setting.deepCopy(user);
  return fetch(`${Setting.ServerUrl}/api/delete-user`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(newUser),
  }).then(res => res.json());
}

export function deleteUsers(userIds) {
  const users = {userid: userIds};
  return fetch(`${Setting.ServerUrl}/api/delete-users`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(users),
  }).then(res => res.json());
}

export function forbiddenUser(userId, isEnable) {
  const postData = {userid: userId, isEnable: isEnable};
  return fetch(`${Setting.ServerUrl}/api/set-user-enable`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(postData),
  }).then(res => res.json());
}

export function getAddressOptions(url) {
  return fetch(url, {
    method: "GET",
  }).then(res => res.json());
}

export function getAffiliationOptions(url, code) {
  return fetch(`${url}/${code}`, {
    method: "GET",
  }).then(res => res.json());
}

export function setPassword(userOwner, userName, oldPassword, newPassword) {
  const formData = new FormData();
  formData.append("userOwner", userOwner);
  formData.append("userName", userName);
  formData.append("oldPassword", oldPassword);
  formData.append("newPassword", newPassword);
  return fetch(`${Setting.ServerUrl}/api/set-password`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then(res => res.json());
}

export function sendCode(checkType, checkId, checkKey, dest, type, applicationId, checkUser) {
  const formData = new FormData();
  formData.append("checkType", checkType);
  formData.append("checkId", checkId);
  formData.append("checkKey", checkKey);
  formData.append("dest", dest);
  formData.append("type", type);
  formData.append("applicationId", applicationId);
  formData.append("checkUser", checkUser);
  return fetch(`${Setting.ServerUrl}/api/send-verification-code`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then(res => res.json()).then(res => {
    if (res.status === "ok") {
      Setting.showMessage("success", i18next.t("user:Code Sent"));
      return true;
    } else {
      Setting.showResponseMessage(res.msg);
      return false;
    }
  });
}

export function verifyCaptcha(captchaType, captchaToken, clientSecret) {
  const formData = new FormData();
  formData.append("captchaType", captchaType);
  formData.append("captchaToken", captchaToken);
  formData.append("clientSecret", clientSecret);
  return fetch(`${Setting.ServerUrl}/api/verify-captcha`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then(res => res.json()).then(res => {
    if (res.status === "ok") {
      if (res.data) {
        Setting.showMessage("success", i18next.t("user:Captcha Verify Success"));
        return true;
      } else {
        Setting.showMessage("error", i18next.t("user:Captcha Verify Failed"));
        return false;
      }
    } else {
      Setting.showMessage("error", i18next.t("user:" + res.msg));
      return false;
    }
  });
}

export function verifyCaptcha2(captchaType, captchaToken, clientSecret) {
  const formData = new FormData();
  formData.append("captchaType", captchaType);
  formData.append("captchaToken", captchaToken);
  formData.append("clientSecret", clientSecret);
  return fetch(`${Setting.ServerUrl}/api/verify-captcha`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then(res => res.json());
}

export function resetEmailOrPhone(dest, type, code) {
  const formData = new FormData();
  formData.append("dest", dest);
  formData.append("type", type);
  formData.append("code", code);
  return fetch(`${Setting.ServerUrl}/api/reset-email-or-phone`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then(res => res.json());
}

export function getCaptcha(owner, applicationUid, isCurrentProvider) {
  return fetch(`${Setting.ServerUrl}/api/get-captcha?applicationId=${owner}/${encodeURIComponent(applicationUid)}&isCurrentProvider=${isCurrentProvider}`, {
    method: "GET",
  }).then(res => res.json()).then(res => res.data);
}

export function updateDepartments(userIds, departments, newOrgan = "") {
  const postData = {users: userIds, departments: departments, newOrgan: newOrgan};
  return fetch(`${Setting.ServerUrl}/api/set-users-depts`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(postData),
  }).then(res => res.json());
}

export function getDepartments(userId) {
  return fetch(`${Setting.ServerUrl}/api/get-user-departments?userId=${userId}`, {
    method: "GET",
    credentials: "include",
  }).then(res => res.json());
}

export function downloadUserTemplate() {
  let fileName = "";
  return fetch(`${Setting.ServerUrl}/api/download-user-template`, {
    method: "GET",
    credentials: "include",
    responseType: "blob",
  }).then(res => {
    if (res.status === 404) {
      return;
    }
    // 获取响应头
    const fileNameEncode = res.headers
      .get("Content-Disposition")
      .split("filename*=utf-8''")[1];
    fileName = decodeURIComponent(fileNameEncode);
    return res.blob().then(blob => {
      FileSaver.saveAs(blob, fileName);
    });
  }).catch(error => {
    Setting.showResponseMessage(error);
  });
}

export async function importUser(file) {
  const formData = new FormData();
  formData.append("import-user-file", file);
  return fetch(`${Setting.ServerUrl}/api/import-user`, {
    body: formData,
    method: "POST",
    credentials: "include",
  }).then(res => res.json());
}

export function downloadImportError(fileName) {
  return fetch(`${Setting.ServerUrl}/api/download-user-import-error?fileName=${fileName}`, {
    method: "GET",
    credentials: "include",
    responseType: "blob",
  }).then(res => {
    if (res.status === 404) {
      return;
    }
    // 获取响应头
    const fileNameEncode = res.headers
      .get("Content-Disposition")
      .split("filename*=utf-8''")[1];
    fileName = decodeURIComponent(fileNameEncode);
    return res.blob().then(blob => {
      FileSaver.saveAs(blob, fileName);
    });
  }).catch(error => {
    Setting.showResponseMessage(error);
  });
}

export function activateUser(username, userOwner) {
  const formData = new FormData();
  formData.append("owner", userOwner);
  formData.append("username", username);
  return fetch(`${Setting.ServerUrl}/api/user-activate`, {
    method: "POST",
    credentials: "include",
    body: formData,
  }).then(res => res.json());
}

export function getRoleUsers(roleId, page = "", pageSize = "", field = "", value = "", sortField = "", sortOrder = "") {
  return fetch(`${Setting.ServerUrl}/api/get-users-with-role?roleId=${roleId}&p=${page}&pageSize=${pageSize}&field=${field}&value=${value}&sortField=${sortField}&sortOrder=${sortOrder}`, {
    method: "GET",
    credentials: "include",
  }).then(res => res.json());
}

// 设置用户的角色
export async function setUserRoles(owner, uid, roles) {
  const body = {userRoleInfos: roles};
  return fetch(`${Setting.ServerUrl}/api/set-user-roles?id=${owner}/${encodeURIComponent(uid)}`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(body),
  }).then(res => res.json());
}

/* 
导出用户 post 请求
如果传递了userId列表，则导出该列表中的所有用户
如果传递了部门uid列表，则导出直属于列表中的部门内的所有用户
如果传递了组织uid，则导出该组织内的所有用户
*/
export function exportUsers(orgUid, params) {
  let fileName = "";
  return fetch(`${Setting.ServerUrl}/api/export-user?organization=${orgUid}`, {
    method: "POST",
    credentials: "include",
    body: JSON.stringify(params),
    responseType: "blob",
  }).then(res => {
    if (res.status === 404) {
      return;
    }
    // 获取响应头
    if (res.headers.get("Content-Disposition")) {
      if (res.headers
        .get("Content-Disposition")
        .split("filename*=utf-8''").length < 2) {
        const fileNameEncode = res.headers
          .get("Content-Disposition")
          .split("filename=")[1];
        fileName = decodeURIComponent(fileNameEncode);
      } else {
        const fileNameEncode = res.headers
          .get("Content-Disposition")
          .split("filename*=utf-8''")[1];
        fileName = decodeURIComponent(fileNameEncode);
      }
    }

    Setting.showMessage("info", i18next.t("导出文件即将下载"));

    return res.blob().then(blob => {
      FileSaver.saveAs(blob, fileName, (a, b, c) => {
        // eslint-disable-next-line no-console
        console.log(a);
        // eslint-disable-next-line no-console
        console.log(b);
        // eslint-disable-next-line no-console
        console.log(c);
      });
    });
  }).catch(error => {
    Setting.showResponseMessage(error);
  });
}
